'use client';

import { ResponsiveModal } from '@/components/ui/ResponsiveModal';
import { IconCityHall, IconPhone } from '@repo/svgs';
import { toTitleCase } from '@/utils/textTransform';

export type ComplaintMonth = { full: string; short: string };

function formatComplaintDateTime(raw: any): string {
  if (typeof raw !== 'string' || !raw.trim()) return '';
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '';
    const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const time = d
      .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      .toLowerCase();
    return `${date}, ${time}`;
  } catch {
    return '';
  }
}

export function ComplaintMonthModal({
  open,
  onClose,
  year,
  month,
  complaints,
}: {
  open: boolean;
  onClose: () => void;
  year: string;
  month: ComplaintMonth;
  complaints: any[];
}) {
  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      label={`Complaints in ${month.short} ${year}`}
      maxWidth="lg"
    >
      <div>
        {complaints.map((c, idx) => {
          const title = (c?.complaint_type ?? c?.complaintType ?? '').toString();
          const status = (c?.status ?? '').toString();
          const descriptor = (c?.descriptor ?? '').toString();
          const locationType = (c?.location_type ?? c?.locationType ?? '').toString();
          const agency = (c?.agency_name ?? c?.agencyName ?? '').toString();
          const created = formatComplaintDateTime(c?.created_date ?? c?.createdDate);

          return (
            <div key={`${idx}`}>
              <div className="flex gap-4 py-3">
                <IconPhone className="w-6 h-6 shrink-0 icon-warning mt-1" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="text-body-2 text-primary">{toTitleCase(title)}</div>
                  <div className="text-caption text-secondary pt-1">
                    {created}
                    {created && status ? ' · ' : ''}
                    {status}
                  </div>
                  {descriptor || locationType ? (
                    <div className="text-caption text-secondary pt-2">
                      {descriptor}
                      {locationType ? ` at ${locationType.toLowerCase()}` : ''}
                    </div>
                  ) : null}
                  {agency ? (
                    <div className="flex items-center gap-2 text-caption text-secondary pt-2">
                      <IconCityHall className="w-4 h-4 shrink-0" aria-hidden />
                      <span className="truncate">{toTitleCase(agency)}</span>
                    </div>
                  ) : null}
                </div>
              </div>
              {idx < complaints.length - 1 ? (
                <div className="border-b border-secondary my-1" />
              ) : null}
            </div>
          );
        })}
      </div>
    </ResponsiveModal>
  );
}
